import { findCountryFlag } from "../../Team/countries";
import { useTranslation } from "react-i18next";
import { useYear } from "../../../../context/YearContext.js";
import StatusMessage from "../../../../components/StatusMessage/StatusMessage.js";

const NationalView = () => {
  const { t } = useTranslation();
  const { year } = useYear();

  // 각 거리별 남자 개인전 우승자 (아래 우승자 목록은 모두 2025 결과)
  const maleWinners = {
    "145M": [
      { rank: 1, country: "RU" },
      { rank: 2, country: "KR" },
      { rank: 3, country: "KR" },
    ],
    "90M": [
      { rank: 1, country: "TH" },
      { rank: 2, country: "MY" },
      { rank: 3, country: "AU" },
    ],
    "70M": [
      { rank: 1, country: "MN" },
      { rank: 2, country: "KZ" },
      { rank: 3, country: "KZ" },
    ],
    "30M": [
      { rank: 1, country: "MN" },
      { rank: 2, country: "KZ" },
      { rank: 3, country: "ID" },
    ],
  };

  // 각 거리별 여자 개인전 우승자
  const femaleWinners = {
    "145M": [
      { rank: 1, country: "KR" },
      { rank: 2, country: "KR" },
      { rank: 3, country: "KR" },
    ],
    "90M": [
      { rank: 1, country: "HU" },
      { rank: 2, country: "AU" },
      { rank: 3, country: "MN" },
    ],
    "70M": [
      { rank: 1, country: "TR" },
      { rank: 2, country: "AU" },
      { rank: 3, country: "TR" },
    ],
    "30M": [
      { rank: 1, country: "KZ" },
      { rank: 2, country: "KZ" },
      { rank: 3, country: "KZ" },
    ],
  };

  // 각 거리별 단체전 우승 팀
  const teamWinners = {
    "145M": [
      { rank: 1, country: "KR" },
      { rank: 2, country: "KR" },
      { rank: 3, country: "KR" },
    ],
    "90M": [
      { rank: 1, country: "KZ" },
      { rank: 2, country: "AU" },
      { rank: 3, country: "HU" },
    ],
    "70M": [
      { rank: 1, country: "TR" },
      { rank: 2, country: "KZ" },
      { rank: 3, country: "KZ" },
    ],
    "30M": [
      { rank: 1, country: "ID" },
      { rank: 2, country: "MY" },
      { rank: 3, country: "RU" },
    ],
  };

  // 개인전 배점
  const INDIVIDUAL_POINTS = {
    1: 300,
    2: 200,
    3: 100,
  };

  // 단체전 배점
  const TEAM_POINTS = {
    1: 700,
    2: 600,
    3: 500,
  };

  // 국가별 점수 계산
  const calculateCountryScores = () => {
    const countryScores = {};

    // 점수 추가 헬퍼 함수
    const addPoints = (country, distance, points, rank) => {
      if (!country) return;

      if (!countryScores[country]) {
        countryScores[country] = {
          country: country,
          scores: { "145M": 0, "90M": 0, "70M": 0, "30M": 0 },
          total: 0,
          gold: 0,
          silver: 0,
          bronze: 0,
        };
      }

      countryScores[country].scores[distance] += points;
      countryScores[country].total += points;

      // 메달 개수 추가
      if (rank === 1) countryScores[country].gold += 1;
      else if (rank === 2) countryScores[country].silver += 1;
      else if (rank === 3) countryScores[country].bronze += 1;
    };

    // 1. 남자 개인전 점수 추가
    Object.keys(maleWinners).forEach((distance) => {
      maleWinners[distance].forEach((result) => {
        const points = INDIVIDUAL_POINTS[result.rank] || 0;
        addPoints(result.country, distance, points, result.rank);
      });
    });

    // 2. 여자 개인전 점수 추가
    Object.keys(femaleWinners).forEach((distance) => {
      femaleWinners[distance].forEach((result) => {
        const points = INDIVIDUAL_POINTS[result.rank] || 0;
        addPoints(result.country, distance, points, result.rank);
      });
    });

    // 3. 단체전 점수 추가
    Object.keys(teamWinners).forEach((distance) => {
      teamWinners[distance].forEach((result) => {
        const points = TEAM_POINTS[result.rank] || 0;
        addPoints(result.country, distance, points, result.rank);
      });
    });

    // 총점 기준으로 정렬
    return Object.values(countryScores).sort((a, b) => {
      // 1. 총점 비교
      if (b.total !== a.total) {
        return b.total - a.total;
      }

      // 2. 동점일 때 금메달 개수 비교
      if (b.gold !== a.gold) {
        return b.gold - a.gold;
      }

      // 3. 금메달도 같으면 은메달 개수 비교
      if (b.silver !== a.silver) {
        return b.silver - a.silver;
      }

      // 4. 은메달도 같으면 동메달 개수 비교
      if (b.bronze !== a.bronze) {
        return b.bronze - a.bronze;
      }

      // 5. 메달도 모두 같으면 국가 코드 알파벳 순
      return a.country.localeCompare(b.country);
    });
  };

  // 순위 계산 함수 (1-3위는 항상 다른 순위)
  const getRankWithTopThree = (countries, index) => {
    // 1-3위는 항상 순차적으로 부여
    if (index < 3) {
      return index + 1;
    }

    // 4위부터는 동점자 처리
    const current = countries[index];

    // 점수가 다르면 새로운 순위
    let rank = index + 1;
    for (let i = index - 1; i >= 3; i--) {
      // 3위(index 2) 이후부터만 비교
      if (countries[i].total === current.total) {
        rank = i + 1;
      } else {
        break;
      }
    }

    return rank;
  };

  const rankedCountries = calculateCountryScores();

  return (
    <>
      <div className="table-box  box-1 margin-table">
        <div className="table-wrap">
          <div className="table-shadow">
            {year !== "2025" ? (
              <StatusMessage />
            ) : (
              <table>
                <colgroup>
                  <col width="5%" />
                  <col width="5%" />
                  <col width="5%" />
                  <col width="5%" />
                  <col width="5%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="15%" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("table.rank")}</th>
                    <th>{t("table.country")}</th>
                    <th style={{ fontSize: "25px" }}>🥇</th>
                    <th style={{ fontSize: "25px" }}>🥈</th>
                    <th style={{ fontSize: "25px" }}>🥉</th>
                    <th>145m</th>
                    <th>90m</th>
                    <th>70m</th>
                    <th>30m</th>
                    <th>{t("table.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rankedCountries.slice(0, 10).map((country, index) => {
                    const rank = getRankWithTopThree(rankedCountries, index);
                    return (
                      <tr key={index} className={index < 3 ? "ranking" : ""}>
                        <td>{rank}</td>
                        <td className="flag-overall">
                          <img
                            className="flag"
                            src={findCountryFlag(country.country)}
                            alt={country.country}
                          />
                          {country.country}
                        </td>
                        <td>{country.gold}</td>
                        <td>{country.silver}</td>
                        <td>{country.bronze}</td>
                        <td>{country.scores["145M"] || "-"}</td>
                        <td>{country.scores["90M"] || "-"}</td>
                        <td>{country.scores["70M"] || "-"}</td>
                        <td>{country.scores["30M"] || "-"}</td>
                        <td>{country.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="table-gradient"></div>
        </div>
      </div>
    </>
  );
};
export default NationalView;
